import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Services.css';

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showSubServiceModal, setShowSubServiceModal] = useState(false);
  const [showRelationsModal, setShowRelationsModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingSubService, setEditingSubService] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientServices, setClientServices] = useState([]);

  const [serviceForm, setServiceForm] = useState({
    service_name: '',
    service_code: '',
    description: '',
    category: '',
    is_active: true,
    display_order: 0,
  });

  const [subServiceForm, setSubServiceForm] = useState({
    service_id: '',
    sub_service_name: '',
    sub_service_code: '',
    description: '',
    has_quantity: false,
    quantity_label: '',
    default_quantity: 1,
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    loadServices();
    loadClients();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await api.getServices(true, false); // with sub-services, all (not just active)
      if (response.success) {
        setServices(response.data || []);
      }
      setError('');
    } catch (err) {
      setError('Failed to load services');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.getClients();
      if (response.success) {
        setClients(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const loadClientServices = async (clientId) => {
    try {
      const response = await api.getClientServices(clientId);
      if (response.success && response.data) {
        setClientServices(response.data);
      } else {
        setClientServices({ services: [], sub_services: [] });
      }
    } catch (err) {
      console.error('Failed to load client services:', err);
      setClientServices({ services: [], sub_services: [] });
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      if (editingService) {
        await api.updateService(editingService.id, serviceForm);
        setSuccess('Service updated successfully');
      } else {
        await api.createService(serviceForm);
        setSuccess('Service created successfully');
      }
      setShowServiceModal(false);
      resetServiceForm();
      loadServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save service');
    }
  };

  const handleSubServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      if (editingSubService) {
        await api.updateSubService(editingSubService.id, subServiceForm);
        setSuccess('Sub-service updated successfully');
      } else {
        await api.createSubService(subServiceForm);
        setSuccess('Sub-service created successfully');
      }
      setShowSubServiceModal(false);
      resetSubServiceForm();
      loadServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save sub-service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service? This will also delete all associated sub-services.')) {
      return;
    }
    try {
      await api.deleteService(id);
      setSuccess('Service deleted successfully');
      loadServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete service');
    }
  };

  const handleDeleteSubService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sub-service?')) {
      return;
    }
    try {
      await api.deleteSubService(id);
      setSuccess('Sub-service deleted successfully');
      loadServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete sub-service');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      service_name: service.service_name || '',
      service_code: service.service_code || '',
      description: service.description || '',
      category: service.category || '',
      is_active: service.is_active === 1 || service.is_active === true,
      display_order: service.display_order || 0,
    });
    setShowServiceModal(true);
  };

  const handleEditSubService = (subService, serviceId) => {
    setEditingSubService(subService);
    setSubServiceForm({
      service_id: serviceId || subService.service_id || '',
      sub_service_name: subService.sub_service_name || '',
      sub_service_code: subService.sub_service_code || '',
      description: subService.description || '',
      has_quantity: subService.has_quantity === 1 || subService.has_quantity === true,
      quantity_label: subService.quantity_label || '',
      default_quantity: subService.default_quantity || 1,
      is_active: subService.is_active === 1 || subService.is_active === true,
      display_order: subService.display_order || 0,
    });
    setShowSubServiceModal(true);
  };

  const handleAddSubService = (serviceId) => {
    setEditingSubService(null);
    setSubServiceForm({
      service_id: serviceId,
      sub_service_name: '',
      sub_service_code: '',
      description: '',
      has_quantity: false,
      quantity_label: '',
      default_quantity: 1,
      is_active: true,
      display_order: 0,
    });
    setShowSubServiceModal(true);
  };

  const handleManageRelations = (service) => {
    setSelectedService(service);
    setShowRelationsModal(true);
  };

  const handleClientChange = async (clientId) => {
    setSelectedClient(clientId);
    if (clientId) {
      await loadClientServices(clientId);
    } else {
      setClientServices([]);
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      service_name: '',
      service_code: '',
      description: '',
      category: '',
      is_active: true,
      display_order: 0,
    });
    setEditingService(null);
  };

  const resetSubServiceForm = () => {
    setSubServiceForm({
      service_id: '',
      sub_service_name: '',
      sub_service_code: '',
      description: '',
      has_quantity: false,
      quantity_label: '',
      default_quantity: 1,
      is_active: true,
      display_order: 0,
    });
    setEditingSubService(null);
  };

  const toggleServiceActive = async (service) => {
    try {
      await api.updateService(service.id, {
        ...service,
        is_active: !(service.is_active === 1 || service.is_active === true),
      });
      loadServices();
    } catch (err) {
      setError('Failed to update service status');
    }
  };

  const toggleSubServiceActive = async (subService) => {
    try {
      await api.updateSubService(subService.id, {
        ...subService,
        is_active: !(subService.is_active === 1 || subService.is_active === true),
      });
      loadServices();
    } catch (err) {
      setError('Failed to update sub-service status');
    }
  };

  if (loading) {
    return (
      <div className="services-page">
        <div className="loading-indicator">Loading services...</div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="page-header">
        <h1>Services Management</h1>
        <button
          className="btn btn-primary add-service-btn"
          onClick={() => {
            resetServiceForm();
            setShowServiceModal(true);
          }}
        >
          <span className="btn-icon">+</span> Add New Service
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Floating Action Button for Mobile */}
      <button
        className="btn btn-primary floating-add-btn"
        onClick={() => {
          resetServiceForm();
          setShowServiceModal(true);
        }}
        title="Add New Service"
      >
        <span className="btn-icon">+</span>
      </button>

      <div className="services-container">
        {services.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔧</div>
            <h2>No Services Yet</h2>
            <p>Get started by creating your first service.</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetServiceForm();
                setShowServiceModal(true);
              }}
            >
              <span className="btn-icon">+</span> Create Your First Service
            </button>
          </div>
        ) : (
          services.map((service) => (
            <div key={service.id} className="service-card">
              <div className="service-header">
                <div className="service-title-group">
                  <h3>{service.service_name}</h3>
                  <span className="service-code">Code: {service.service_code}</span>
                  {service.category && (
                    <span className="service-category">{service.category}</span>
                  )}
                </div>
                <div className="service-actions">
                  <button
                    className={`btn btn-sm ${service.is_active === 1 || service.is_active === true ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => toggleServiceActive(service)}
                    title={service.is_active === 1 || service.is_active === true ? 'Active' : 'Inactive'}
                  >
                    {service.is_active === 1 || service.is_active === true ? '✓' : '○'}
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditService(service)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteService(service.id)}
                  >
                    Delete
                  </button>
                  <button
                    className="btn btn-sm btn-info"
                    onClick={() => handleManageRelations(service)}
                  >
                    Relations
                  </button>
                </div>
              </div>

              {service.description && (
                <p className="service-description">{service.description}</p>
              )}

              <div className="sub-services-section">
                <div className="sub-services-header">
                  <h4>Sub-Services ({service.sub_services?.length || 0})</h4>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleAddSubService(service.id)}
                  >
                    + Add Sub-Service
                  </button>
                </div>

                {service.sub_services && service.sub_services.length > 0 ? (
                  <div className="sub-services-list">
                    {service.sub_services.map((subService) => (
                      <div key={subService.id} className="sub-service-item">
                        <div className="sub-service-info">
                          <span className="sub-service-name">{subService.sub_service_name}</span>
                          <span className="sub-service-code">({subService.sub_service_code})</span>
                          {subService.has_quantity === 1 && (
                            <span className="sub-service-badge">Quantity-based</span>
                          )}
                          {subService.is_active !== 1 && subService.is_active !== true && (
                            <span className="sub-service-badge inactive">Inactive</span>
                          )}
                        </div>
                        <div className="sub-service-actions">
                          <button
                            className={`btn btn-xs ${subService.is_active === 1 || subService.is_active === true ? 'btn-success' : 'btn-secondary'}`}
                            onClick={() => toggleSubServiceActive(subService)}
                            title={subService.is_active === 1 || subService.is_active === true ? 'Active' : 'Inactive'}
                          >
                            {subService.is_active === 1 || subService.is_active === true ? '✓' : '○'}
                          </button>
                          <button
                            className="btn btn-xs btn-primary"
                            onClick={() => handleEditSubService(subService, service.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-xs btn-danger"
                            onClick={() => handleDeleteSubService(subService.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-sub-services">No sub-services yet</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
              <button className="modal-close" onClick={() => setShowServiceModal(false)}>×</button>
            </div>
            <form onSubmit={handleServiceSubmit}>
              <div className="form-group">
                <label>Service Name *</label>
                <input
                  type="text"
                  value={serviceForm.service_name}
                  onChange={(e) => setServiceForm({ ...serviceForm, service_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Service Code *</label>
                <input
                  type="text"
                  value={serviceForm.service_code}
                  onChange={(e) => setServiceForm({ ...serviceForm, service_code: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={serviceForm.category}
                    onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={serviceForm.display_order}
                    onChange={(e) => setServiceForm({ ...serviceForm, display_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceForm.is_active}
                    onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowServiceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingService ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sub-Service Modal */}
      {showSubServiceModal && (
        <div className="modal-overlay" onClick={() => setShowSubServiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubService ? 'Edit Sub-Service' : 'Add New Sub-Service'}</h2>
              <button className="modal-close" onClick={() => setShowSubServiceModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubServiceSubmit}>
              <div className="form-group">
                <label>Parent Service *</label>
                <select
                  value={subServiceForm.service_id}
                  onChange={(e) => setSubServiceForm({ ...subServiceForm, service_id: e.target.value })}
                  required
                  disabled={!!editingSubService}
                >
                  <option value="">Select Service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.service_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Sub-Service Name *</label>
                <input
                  type="text"
                  value={subServiceForm.sub_service_name}
                  onChange={(e) => setSubServiceForm({ ...subServiceForm, sub_service_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Sub-Service Code *</label>
                <input
                  type="text"
                  value={subServiceForm.sub_service_code}
                  onChange={(e) => setSubServiceForm({ ...subServiceForm, sub_service_code: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={subServiceForm.description}
                  onChange={(e) => setSubServiceForm({ ...subServiceForm, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={subServiceForm.has_quantity}
                    onChange={(e) => setSubServiceForm({ ...subServiceForm, has_quantity: e.target.checked })}
                  />
                  Has Quantity
                </label>
              </div>
              {subServiceForm.has_quantity && (
                <>
                  <div className="form-group">
                    <label>Quantity Label</label>
                    <input
                      type="text"
                      value={subServiceForm.quantity_label}
                      onChange={(e) => setSubServiceForm({ ...subServiceForm, quantity_label: e.target.value })}
                      placeholder="e.g., Pages, Items, etc."
                    />
                  </div>
                  <div className="form-group">
                    <label>Default Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={subServiceForm.default_quantity}
                      onChange={(e) => setSubServiceForm({ ...subServiceForm, default_quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={subServiceForm.display_order}
                    onChange={(e) => setSubServiceForm({ ...subServiceForm, display_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={subServiceForm.is_active}
                      onChange={(e) => setSubServiceForm({ ...subServiceForm, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSubServiceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSubService ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Relations Modal */}
      {showRelationsModal && selectedService && (
        <div className="modal-overlay" onClick={() => setShowRelationsModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Relationships - {selectedService.service_name}</h2>
              <button className="modal-close" onClick={() => setShowRelationsModal(false)}>×</button>
            </div>
            <div className="relations-content">
              <div className="relations-section">
                <h3>Client Assignments</h3>
                <div className="form-group">
                  <label>Select Client</label>
                  <select
                    value={selectedClient}
                    onChange={(e) => handleClientChange(e.target.value)}
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.client_id} value={client.client_id}>
                        {client.client_name}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedClient && (
                  <div className="client-services-info">
                    <h4>Assigned Services for Selected Client</h4>
                    {clientServices.services && clientServices.services.length > 0 ? (
                      <ul>
                        {clientServices.services.map((s) => (
                          <li key={s.id}>
                            {s.service_name} {s.service_code && `(${s.service_code})`}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No services assigned to this client</p>
                    )}
                    {clientServices.sub_services && clientServices.sub_services.length > 0 && (
                      <>
                        <h4 style={{ marginTop: '15px' }}>Assigned Sub-Services</h4>
                        <ul>
                          {clientServices.sub_services.map((ss) => (
                            <li key={ss.id}>
                              {ss.sub_service_name} {ss.sub_service_code && `(${ss.sub_service_code})`}
                              {ss.quantity && ` - Qty: ${ss.quantity}`}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="relations-section">
                <h3>Sub-Services</h3>
                <p>This service has {selectedService.sub_services?.length || 0} sub-service(s)</p>
                {selectedService.sub_services && selectedService.sub_services.length > 0 && (
                  <ul>
                    {selectedService.sub_services.map((ss) => (
                      <li key={ss.id}>
                        {ss.sub_service_name} ({ss.sub_service_code})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRelationsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;

